import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Alert, Badge, Button, Col, Row } from 'reactstrap';
import Icon from '../icon';
import ModalImageCropper from '../modals/modal-image-cropper';

const DropzonePreviewComponent = ({
  multipleCoverContent,
  setMultipleCoverContent,
  setDeletedCoverContents,
  handleCroppedImageUpload,
  onRemoveLocalFile
}) => {
  const [cropperData, setCropperData] = React.useState({ isOpen: false, image: null, index: null });

  const openCropper = (image, index) => {
    setCropperData({ isOpen: true, image, index });
  };

  const closeCropper = () => {
    setCropperData({ isOpen: false, image: null, index: null });
  };

  const onCropComplete = blob => {
    handleCroppedImageUpload(blob, cropperData.index);
    closeCropper();
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result.map((obj, idx) => ({ ...obj, position: `${idx}` }));
  };

  const onDragEndMultipleCoverContent = result => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const res = reorder(multipleCoverContent, result.source.index, result.destination.index);
    setMultipleCoverContent(res);
  };

  const removeCoverContent = index => {
    const newContent = [...multipleCoverContent];
    const [deletedContent] = newContent.splice(index, 1);
    const res = newContent.map((obj, idx) => ({ ...obj, position: `${idx}` }));
    setMultipleCoverContent(res);
    if (!deletedContent?.[0]?.isNew || deletedContent?.[0]?.editImage?.id) {
      if (setDeletedCoverContents) {
        setDeletedCoverContents(prev => [...prev, deletedContent]);
      }
    }

    if (deletedContent?.isLocal && onRemoveLocalFile) {
      onRemoveLocalFile(deletedContent.id);
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEndMultipleCoverContent}>
      <Droppable droppableId="multipleCoverContentList">
        {provided => (
          <ul className="link-block no-bullets" {...provided.droppableProps} ref={provided.innerRef}>
            {multipleCoverContent.map((item, index) => {
              if (item.position) {
                return (
                  <Draggable
                    draggableId={item.id || item.editImage?.id || item.videoId || item.url}
                    index={index}
                    key={item.id || item.editImage?.id || item.videoId || item.url}
                  >
                    {providedChild => (
                      <li
                        ref={providedChild.innerRef}
                        {...providedChild.draggableProps}
                        {...providedChild.dragHandleProps}
                      >
                        <Alert color="light" className="py-5 px-10 mb-10">
                          <Row>
                            <Col xs="3" className="d-flex align-items-center my-5 position-relative">
                              {((item?.file?.mimetype || item?.file?.type || item?.type || '').includes('video') || item?.videoId || item?.value) && (
                                <>
                                  {item?.file?.url || item?.url || (item?.file && (item?.file instanceof window.File || item?.file instanceof window.Blob)) ? (
                                    <video
                                      style={{
                                        width: '100%',
                                        borderRadius: 12,
                                        cursor: 'pointer',
                                        maxHeight: '400px'
                                      }}
                                      autoPlay
                                      loop
                                      playsInline
                                      muted
                                    >
                                      <source
                                        src={
                                          item?.file?.url ||
                                          item?.url ||
                                          (item?.file && (item?.file instanceof window.File || item?.file instanceof window.Blob) ? URL.createObjectURL(item.file) : '')
                                        }
                                        type={item?.file?.type || item?.file?.mimetype}
                                      />
                                      <track kind="captions" src="" label="No captions available" />
                                    </video>
                                  ) : (
                                    <div
                                      className="d-flex align-items-center justify-content-center bg-light border rounded"
                                      style={{ width: '100%', minHeight: '100px' }}
                                    >
                                      <Icon name="video" size={32} color="#adb5bd" />
                                    </div>
                                  )}
                                </>
                              )}
                              {!((item?.file?.mimetype || item?.file?.type || item?.type || '').includes('video') || item?.videoId || item?.value) && (item?.url || item?.editImage?.url || item?.file) && (
                                <div className="position-relative w-100">
                                  <div
                                    className="img-round border rounded overflow-hidden"
                                    style={{
                                      width: '100%',
                                      borderRadius: 12,
                                      cursor: 'pointer',
                                      maxHeight: '400px'
                                    }}
                                    onClick={() =>
                                      item.isLocal && openCropper(item?.url || item?.editImage?.url || URL.createObjectURL(item.file), index)
                                    }
                                    onKeyDown={e => {
                                      if ((e.key === 'Enter' || e.key === ' ') && item.isLocal) {
                                        openCropper(
                                          item?.url || item?.editImage?.url || URL.createObjectURL(item.file),
                                          index
                                        );
                                      }
                                    }}
                                    role={item.isLocal ? "button" : undefined}
                                    tabIndex={item.isLocal ? "0" : undefined}
                                  >
                                    <img
                                      alt={(item?.url || item?.editImage?.url || item?.file?.name || '').split('/').at(-1)}
                                      src={item?.url || item?.editImage?.url || item?.file?.url || (item.file && (item.file instanceof window.File || item.file instanceof window.Blob) ? URL.createObjectURL(item.file) : '')}
                                      style={{
                                        width: '100%',
                                        display: 'block'
                                      }}
                                    />
                                  </div>
                                  {item.isLocal && (
                                    <div
                                      className="position-absolute"
                                      style={{
                                        top: 5,
                                        right: 5,
                                        background: 'rgba(0,0,0,0.6)',
                                        borderRadius: '6px',
                                        padding: '2px 8px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        openCropper(item?.url || item?.editImage?.url || URL.createObjectURL(item.file), index);
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.stopPropagation();
                                          openCropper(
                                            item?.url || item?.editImage?.url || URL.createObjectURL(item.file),
                                            index
                                          );
                                        }
                                      }}
                                      role="button"
                                      tabIndex="0"
                                    >
                                      <Icon name="crop" className="text-white" size={12} />
                                      <span className="text-white ml-5" style={{ fontSize: '10px', fontWeight: 'bold' }}>
                                        CROP
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Col>

                            <Col xs="8" className="pl-0">
                              {item?.file?.filename ||
                                item?.file?.name ||
                                (item?.url || item?.editImage?.url || '').split('/').at(-1)}
                              {item.isNew && (
                                <Badge color="success" className="ml-5">
                                  New
                                </Badge>
                              )}
                            </Col>
                            <Col xs="1" className="pl-0">
                              <Button
                                color="danger"
                                className="text-center bg-transparent border-0"
                                type="button"
                                onClick={() => removeCoverContent(index)}
                              >
                                <Icon name="trash" className="text-danger" />
                              </Button>
                            </Col>
                          </Row>
                        </Alert>
                      </li>
                    )}
                  </Draggable>
                );
              }
            })}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
      <ModalImageCropper
        isOpen={cropperData.isOpen}
        image={cropperData.image}
        onCropComplete={onCropComplete}
        onCancel={closeCropper}
      />
    </DragDropContext>
  );
};

export default DropzonePreviewComponent;
